import axios from '../utils/axiosConfig';
import { JwilUpdate, ApiResponse, ApiListResponse } from '../types/contentManagement';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

class JwilUpdatesService {
  private baseURL = `${API_BASE_URL}/jwil-updates`;

  // Get all JWIL Updates
  async getAllUpdates(includeHidden: boolean = false): Promise<JwilUpdate[]> {
    try {
      const url = includeHidden ? `${this.baseURL}?includeHidden=true` : this.baseURL;
      const response = await axios.get<ApiListResponse<JwilUpdate>>(url);
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to fetch JWIL updates');
    } catch (error) {
      console.error('Error fetching JWIL updates:', error);
      throw error;
    }
  }

  // Get visible JWIL Updates (for homepage)
  async getVisibleUpdates(): Promise<JwilUpdate[]> {
    return this.getAllUpdates(false);
  }

  // Get JWIL Update by ID
  async getUpdateById(id: number): Promise<JwilUpdate> {
    try {
      const response = await axios.get<ApiResponse<JwilUpdate>>(`${this.baseURL}/${id}`);
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to fetch JWIL update');
    } catch (error) {
      console.error('Error fetching JWIL update:', error);
      throw error;
    }
  }

  // Create new JWIL Update
  async createUpdate(formData: FormData): Promise<JwilUpdate> {
    try {
      const response = await axios.post<ApiResponse<JwilUpdate>>(this.baseURL, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to create JWIL update');
    } catch (error) {
      console.error('Error creating JWIL update:', error);
      throw error;
    }
  }

  // Update JWIL Update
  async updateUpdate(id: number, formData: FormData): Promise<JwilUpdate> {
    try {
      const response = await axios.put<ApiResponse<JwilUpdate>>(`${this.baseURL}/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to update JWIL update');
    } catch (error) {
      console.error('Error updating JWIL update:', error);
      throw error;
    }
  }

  // Delete JWIL Update
  async deleteUpdate(id: number): Promise<void> {
    try {
      const response = await axios.delete<ApiResponse<null>>(`${this.baseURL}/${id}`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete JWIL update');
      }
    } catch (error) {
      console.error('Error deleting JWIL update:', error);
      throw error;
    }
  }

  // Toggle visibility
  async toggleVisibility(id: number): Promise<JwilUpdate> {
    try {
      const response = await axios.patch<ApiResponse<JwilUpdate>>(`${this.baseURL}/${id}/toggle-visibility`);
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to toggle visibility');
    } catch (error) {
      console.error('Error toggling visibility:', error);
      throw error;
    }
  }

  // Helper method to get image URL
  getImageUrl(imagePath: string | undefined | null): string {
    if (!imagePath || typeof imagePath !== 'string') {
      return '/assets/images/default-update.jpg';
    }
    
    // If it's already a full URL, return as is
    if (imagePath.startsWith('http')) {
      return imagePath;
    }
    
    // If it's a relative path starting with /uploads or /assets, return as is (proxy will handle it)
    if (imagePath.startsWith('/uploads') || imagePath.startsWith('/assets')) {
      return imagePath;
    }
    
    // Otherwise assume it's just a filename, prepend /uploads/
    return `/uploads/${imagePath}`;
  }
}

export default new JwilUpdatesService();
